import clsx from "clsx";
import {
  createContext,
  FC,
  Fragment,
  PropsWithChildren,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";
import { Tooltip } from "react-tooltip";
import { fetchQuery, graphql, useRelayEnvironment } from "react-relay";
import {
  Balance,
  BTC,
  GAIA,
  networkLabel,
  priceFormatter,
  THOR,
} from "rujira.js";
import {
  Animated,
  AssetLabel,
  Button,
  Decimal,
  Fiat,
  IconDenom,
  Icons,
  NetworkIcon,
  nFormatter,
  Popout,
  ProviderIcon,
  sortByValueUsd,
  SortDir,
  SortableTableHeader,
  TableFrame,
  TablePagination,
  TranslationProvider,
  useGlobalModalContext,
  useLocalStorage,
  useTablePagination,
  useTableSort,
  useTranslation,
  Warning,
  compareBigInts,
  compareNullableBigInts,
  compareNullableNumbers,
  compareNumbers,
  compareStrings,
} from "rujira.ui";

import Anim from "../../common/assets/cosmos.svg";

import { BalanceContextFragment$data } from "../../common/components/__generated__/BalanceContextFragment.graphql";
import {
  BanxaOffRamp,
  BanxaSellCapabilitiesProvider,
  useBanxaSellCapabilities,
} from "../../common/components/BanxaOffRamp";
import { getBanxaSellSources } from "../../services/banxaSellSources";
import {
  BalanceProvider,
  usePrivateBalanceRows,
  usePreloadedBalance,
  usePreloadedBalanceData,
  useUnifiedPrivateRows,
  type UnifiedPrivateRow,
} from "../../common/components/Balance";
import { useRedactedV2 } from "../../redacted/v2";
import {
  useDeposit,
  useDepositToSubWallet,
  useWithdrawFromSubWallet,
  useWithdrawToPublicAccount,
} from "../../common/components/Deposit";
import { TransferSession } from "../../common/components/TransferSession";
import { Link } from "../../Gate";
import { useIsMobile } from "../../hooks/useBreakpoint";
import { lendRoute } from "../../lending/routes";
import { useWalletAccounts } from "../../services/accounts";
import { isSunsetSymbol } from "../../services/asset";
import {
  assetToLendUrlSegment,
  assetToTradeUrlSegment,
} from "../../services/assetUrl";
import { stakeRoute } from "../../staking/routes";
import { usePortfolio } from "../Portfolio";
import { showsPrivateBalance } from "./privateRows";
import { BalancesTokenActionsQuery } from "./__generated__/BalancesTokenActionsQuery.graphql";
import { Send } from "./actions/Send";
import { Withdraw } from "./actions/Withdraw";
import { useRedacted } from "../../services/redacted";
import PrivacyFlowModal from "./PrivacyFlowModal";
import { privacyAssetsOf } from "./privacy/privacyAssets";
import {
  PrivacyLearnMore,
  PrivacyLearnMoreButton,
} from "./privacy/PrivacyLearnMore";
import { PrivacyMeter } from "./privacy/PrivacyMeter";
const { Chart, Lend, Merge, MoneyTransfer, Stake, Swap, TrendDown, TrendUp } =
  Icons;
import BanxaIcon from "../../common/assets/banxa.webp";

type Success = Extract<
  BalanceContextFragment$data["balancesV2"],
  { ok: true }
>["value"];

type Failure = Extract<
  BalanceContextFragment$data["balancesV2"],
  { ok: false }
>["errors"];

type BalanceEdge = NonNullable<Success["edges"]>[number];
type PortfolioBalance = NonNullable<NonNullable<BalanceEdge>["node"]>;

const BALANCE_SORT_KEYS = [
  "token",
  "networks",
  "balance",
  "price",
  "change24h",
  "value",
] as const;

type BalanceSortKey = (typeof BALANCE_SORT_KEYS)[number];

const BALANCE_COLUMNS = [
  { id: "logo", width: "3rem" },
  { id: "token", width: "calc(18% - 3rem)", minWidth: "10rem" },
  { id: "networks", width: "17%", minWidth: "9rem" },
  { id: "balance", width: "14%", minWidth: "9rem" },
  { id: "price", width: "12%", minWidth: "8rem" },
  { id: "change24h", width: "12%", minWidth: "8rem" },
  { id: "value", width: "17%", minWidth: "10rem" },
  { id: "actions", width: "10%", minWidth: "7rem" },
] as const;

const BALANCE_MOBILE_COLUMNS = BALANCE_COLUMNS.map((column) => ({
  id: column.id,
  width:
    column.id === "token"
      ? "7.5rem"
      : column.id === "networks"
        ? "6.5rem"
        : "minWidth" in column
          ? column.minWidth
          : column.width,
}));

const balanceDefaultSortDir = (sortBy: BalanceSortKey): SortDir =>
  sortBy === "token" ? SortDir.Asc : SortDir.Desc;

const comparePortfolioBalances = (
  a: PortfolioBalance,
  b: PortfolioBalance,
  sortBy: BalanceSortKey,
  sortDir: SortDir
): number => {
  switch (sortBy) {
    case "token":
      return compareStrings(
        a.asset.metadata.symbol,
        b.asset.metadata.symbol,
        sortDir
      );
    case "networks":
      return compareNumbers(a.accounts.length, b.accounts.length, sortDir);
    case "balance":
      return compareBigInts(a.balance, b.balance, sortDir);
    case "price":
      return compareNullableBigInts(
        a.asset.price?.current,
        b.asset.price?.current,
        sortDir
      );
    case "change24h":
      return compareNullableNumbers(
        a.asset.price?.changeDay,
        b.asset.price?.changeDay,
        sortDir
      );
    case "value":
      return compareBigInts(a.valueUsd, b.valueUsd, sortDir);
  }
};

const tokenActionsQuery = graphql`
  query BalancesTokenActionsQuery {
    ghostCredit {
      collaterals {
        asset {
          chain
          metadata {
            symbol
          }
        }
      }
    }
    thorchainV2 {
      pools {
        status
        asset {
          metadata {
            symbol
          }
          variants {
            secured {
              metadata {
                symbol
              }
            }
          }
        }
      }
    }
    finV2(first: 100, sortBy: VOLUME_USD, sortDir: DESC) {
      edges {
        node {
          assetBase {
            chain
            type
            metadata {
              symbol
            }
          }
          assetQuote {
            chain
            type
            metadata {
              symbol
            }
          }
        }
      }
    }
    strategies(first: 100, typenames: ["GhostVault"]) {
      edges {
        node {
          __typename
          ... on GhostVault {
            asset {
              asset
              chain
              type
              metadata {
                symbol
              }
            }
          }
        }
      }
    }
  }
`;

type TokenActionRoutes = {
  borrowPath: string | null;
  lendPath: string | null;
  stakePath: string | null;
  swapPath: string | null;
  tradePath: string | null;
};

type TokenActionData = {
  borrowSymbols: Set<string>;
  lendPaths: Map<string, string>;
  swapSymbols: Set<string>;
  tradePaths: Map<string, string>;
};

const EMPTY_TOKEN_ACTION_DATA: TokenActionData = {
  borrowSymbols: new Set(),
  lendPaths: new Map(),
  swapSymbols: new Set(),
  tradePaths: new Map(),
};

const tokenActionDataContext = createContext<TokenActionData>(
  EMPTY_TOKEN_ACTION_DATA
);

export const Balances: FC = () => {
  const { isPrivateMode } = useRedacted();
  const v2 = useRedactedV2();
  const data = usePreloadedBalanceData();

  return data ? (
    data.ok ? (
      isPrivateMode ? (
        v2.modelB.enabled ? (
          <UnifiedPrivateList />
        ) : (
          <PrivateAccountList />
        )
      ) : data.value.edges?.length === 0 ? (
        <div className="relative card p-3 mt-2">
          <Empty />
        </div>
      ) : (
        <List data={data.value} />
      )
    ) : (
      <div className="relative card p-3 mt-2">
        <Failure errors={data.errors} />
      </div>
    )
  ) : (
    <Loading />
  );
};

const PrivateAccountList: FC = () => {
  const { hideSmall } = usePortfolio();
  const rows = usePrivateBalanceRows();
  const filtered = rows.filter((row) => showsPrivateBalance(row, hideSmall));

  return filtered.length ? (
    <div className="relative card p-3 mt-2">
      <div className="table-sticky table-sticky--first">
        <table className="table table--big condensed portfolio__balances">
          <thead>
            <tr>
              <th className="w-1 bg-black"></th>
              <th style={{ paddingLeft: 0 }}>Token</th>
              <th className="text-right">Private Reserve</th>
              <th className="text-center">Transfer</th>
              <th className="text-right">Spending</th>
              <th className="text-right">Price</th>
              <th className="text-right">24h Change</th>
              <th className="text-right">Total Value</th>
              <th className="w-1">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortByValueUsd(filtered).map((row) => (
              <BalanceProvider asset={row.asset} key={row.asset.asset}>
                <PrivateAccountBalanceItem />
              </BalanceProvider>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  ) : (
    <div className="relative card p-3 mt-2">
      <Empty isLowRune={false} />
    </div>
  );
};

/**
 * Model B: one private wallet, shown exactly like public Rujira. Each token is
 * a single merged balance (Reserve plus anything a one-time sub still holds
 * until it is swept back); the mechanics stay invisible. The Privacy column
 * sits where the public list shows networks; its (i) and each meter open
 * "Learn more about your privacy".
 */
const UnifiedPrivateList: FC = () => {
  const { hideSmall } = usePortfolio();
  const { t } = useTranslation("portfolio");
  const rows = useUnifiedPrivateRows();
  const filtered = rows.filter((row) => showsPrivateBalance(row, hideSmall));
  const privacyAssets = useMemo(() => privacyAssetsOf(rows), [rows]);

  return filtered.length ? (
    <TokenActionRoutesProvider>
      <PrivacyLearnMore assets={privacyAssets}>
        <div className="relative card p-3 mt-2">
          <div className="table-sticky table-sticky--first">
            <table className="table table--big condensed portfolio__balances">
              <thead>
                <tr>
                  <th className="w-1 bg-black"></th>
                  <th style={{ paddingLeft: 0 }}>{t("token")}</th>
                  <th className="auto">
                    <PrivacyLearnMoreButton />
                  </th>
                  <th className="text-right">{t("common:balance")}</th>
                  <th className="text-right">{t("price")}</th>
                  <th className="text-right">{t("change24h")}</th>
                  <th className="text-right">{t("value")}</th>
                  <th className="w-1">{t("actions")}</th>
                </tr>
              </thead>
              <tbody>
                {sortByValueUsd(filtered).map((row) => (
                  <BalanceProvider asset={row.asset} key={row.asset.asset}>
                    <UnifiedPrivateItem row={row} />
                  </BalanceProvider>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </PrivacyLearnMore>
    </TokenActionRoutesProvider>
  ) : (
    <div className="relative card p-3 mt-2">
      <Empty isLowRune={false} />
    </div>
  );
};

const UnifiedPrivateItem: FC<{ row: UnifiedPrivateRow }> = ({ row }) => {
  const { asset, amount, valueUsd } = row;
  const price = asset.price;
  const { proxyBalance } = usePreloadedBalance();
  return (
    <tr>
      <th className="bg-black">
        <IconDenom denom={asset.metadata.symbol} />
      </th>
      <td style={{ paddingLeft: 0 }} className="portfolio__token">
        <AssetLabel
          asset={asset}
          Container={({ children }) => (
            <span className="color-grey fs-14 fs-lg-16">{children}</span>
          )}
        />
      </td>
      <td className="auto">
        <PrivacyMeter
          denom={asset.variants?.native?.denom}
          symbol={asset.metadata.symbol}
        />
      </td>
      <td className="text-right">
        <Decimal amount={amount} round={6} />
      </td>
      <td className="text-right">
        {price?.current ? <>${priceFormatter(price.current)}</> : null}
      </td>
      <td className="text-right">
        <div
          className={clsx({
            "flex ai-c jc-e": true,
            "color-teal": price?.changeDay && price.changeDay > 0,
            "color-red": price?.changeDay && price.changeDay < 0,
          })}>
          {price?.changeDay ? (
            <>
              {price.changeDay.toLocaleDecimal(2)}%
              {price.changeDay > 0 ? (
                <TrendUp className="h-2 w-a ml-0.5" />
              ) : (
                <TrendDown className="h-2 w-a ml-0.5" />
              )}
            </>
          ) : null}
        </div>
      </td>
      <td className="text-right">
        <Fiat amount={valueUsd} decimals={8} symbol="$" padSymbol={false} />
      </td>
      <td className="text-right">
        <Popout buttonClassName="button--outline button--grey mr-0.5">
          <TokenActions
            balance={
              proxyBalance ?? {
                asset,
                balance: amount,
                valueUsd,
                accounts: [],
              }
            }
          />
        </Popout>
      </td>
    </tr>
  );
};

const List: FC<{
  data: Success;
}> = ({ data }) => {
  const { hideSmall } = usePortfolio();
  const isMobile = useIsMobile();
  const { t } = useTranslation("portfolio");
  const { sortBy, sortDir, toggleSort } = useTableSort<BalanceSortKey>({
    initialBy: "value",
    initialDir: SortDir.Desc,
    defaultDir: balanceDefaultSortDir,
  });

  const allBalances = useMemo(
    () => data.edges?.flatMap((edge) => (edge?.node ? [edge.node] : [])) || [],
    [data.edges]
  );
  const isLowRune = allBalances.some(
    ({ asset, balance }) =>
      asset.metadata.symbol === "RUNE" && balance < 2000000n
  );
  const balances = useMemo(
    () =>
      allBalances.filter(
        ({ asset, valueUsd }) =>
          !isSunsetSymbol(asset.metadata.symbol) &&
          (!hideSmall || valueUsd > 100000000n)
      ),
    [allBalances, hideSmall]
  );
  const sortedBalances = useMemo(
    () =>
      [...balances].sort((a, b) => {
        const comparison = comparePortfolioBalances(a, b, sortBy, sortDir);
        return comparison || a.asset.asset.localeCompare(b.asset.asset);
      }),
    [balances, sortBy, sortDir]
  );
  const { pageRows: visibleBalances, pagination } = useTablePagination(
    sortedBalances,
    { resetKey: `${sortBy}:${sortDir}:${hideSmall}` }
  );
  const sortHeaderProps = { active: sortBy, dir: sortDir, onSort: toggleSort };
  const columns = isMobile ? BALANCE_MOBILE_COLUMNS : BALANCE_COLUMNS;

  return balances.length ? (
    <>
      <BanxaSellCapabilitiesProvider>
        <TokenActionRoutesProvider>
          <div className="relative card p-3 mt-2">
            {isLowRune && <LowRuneNotice network={GAIA} symbol="ATOM" />}
            <TableFrame
              className="portfolio-balances-table"
              columns={columns}
              stickyFirstColumn
              tableClassName="table table--fixed table--big condensed portfolio__balances mb-0">
              <thead>
                <tr>
                  <th aria-hidden="true" />
                  <SortableTableHeader
                    {...sortHeaderProps}
                    value="token"
                    label={t("token")}
                    align="left"
                    style={{ paddingLeft: 0 }}
                  />
                  <SortableTableHeader
                    {...sortHeaderProps}
                    value="networks"
                    label={t("networks")}
                  />
                  <SortableTableHeader
                    {...sortHeaderProps}
                    value="balance"
                    label={t("common:balance")}
                    align="right"
                  />
                  <SortableTableHeader
                    {...sortHeaderProps}
                    value="price"
                    label={t("price")}
                    align="right"
                  />
                  <SortableTableHeader
                    {...sortHeaderProps}
                    value="change24h"
                    label={t("change24h")}
                    align="right"
                  />
                  <SortableTableHeader
                    {...sortHeaderProps}
                    value="value"
                    label={t("value")}
                    align="right"
                  />
                  <th scope="col" className="text-right">
                    {t("actions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                <BalanceProvider
                  asset={{
                    type: "LAYER_1",
                    chain: "BTC",
                    asset: "BTC.BTC",
                    metadata: {
                      symbol: "BTC",
                      decimals: 8,
                    },
                  }}>
                  <KeplrBtcWarning />
                </BalanceProvider>
                {visibleBalances.map((entry) => {
                  return (
                    <BalanceProvider key={entry.id} asset={entry.asset}>
                      <BalanceItem />
                    </BalanceProvider>
                  );
                })}
              </tbody>
            </TableFrame>
            <TablePagination {...pagination} />
          </div>
        </TokenActionRoutesProvider>
      </BanxaSellCapabilitiesProvider>
    </>
  ) : (
    <div className="relative card p-3 mt-2">
      <Empty isLowRune={isLowRune} />
    </div>
  );
};

const Failure: FC<{ errors: Failure }> = ({ errors }) => {
  return (
    <>
      {errors.map((e, idx) =>
        e &&
        typeof e === "object" &&
        "status" in e &&
        typeof e.status === "string" ? (
          <Warning key={idx} color="red" className="condensed mt-2">
            <Icons.ExclamationTriangle className="color-red" />
            <span className="warning__msg">{e.status}</span>
          </Warning>
        ) : null
      )}
    </>
  );
};

const Empty: FC<{ isLowRune?: boolean }> = ({ isLowRune = true }) => {
  const { isPrivateMode } = useRedacted();
  const { t } = useTranslation("portfolio");
  const { showModal } = useGlobalModalContext();
  const showPrivacyGuide = () =>
    showModal({
      title: "",
      className: "modal--lg privacy-guide-modal",
      backgroundClose: true,
      children: <PrivacyFlowModal />,
    });
  return (
    <>
      {isLowRune && <LowRuneNotice network={GAIA} symbol="ATOM" />}
      <Animated
        name="portfolio"
        aria-label="No balances"
        className="block w-10 h-10 mx-a mb-1 color-grey"
      />
      <p className="color-grey fw-500 text-center">
        {t("noAvailableBalances")}
      </p>
      <p className="flex jc-c ai-c gap-x-1 mt-2 color-grey">
        {isPrivateMode && (
          <Button
            onClick={showPrivacyGuide}
            className="button--sm button--icon-right"
            label="How Private Mode works"
          />
        )}
        <Button
          onClick={() => document.getElementById("deposit")?.click()}
          className="button--sm button--icon-right button--outline"
          label={t("depositNow")}>
          <Icons.ArrowUpRight />
        </Button>
      </p>
    </>
  );
};

const PrivateAccountBalanceItem: FC = () => {
  const { subBalance, proxyBalance, asset } = usePreloadedBalance();
  const spendable = subBalance?.balance ?? 0n;
  const reserve = proxyBalance?.balance ?? 0n;
  const totalValueUsd =
    (subBalance?.valueUsd ?? 0n) + (proxyBalance?.valueUsd ?? 0n);
  const price = asset.price;
  const makeSpendable = useDepositToSubWallet(0n, asset);
  const moveToReserve = useWithdrawFromSubWallet(0n, asset);

  if (!proxyBalance && !subBalance) return null;

  return (
    <tr>
      <th className="bg-black">
        <IconDenom denom={asset.metadata.symbol} />
      </th>
      <td style={{ paddingLeft: 0 }} className="portfolio__token">
        <AssetLabel
          asset={asset}
          Container={({ children }) => (
            <span className="color-grey fs-14 fs-lg-16">{children}</span>
          )}
        />
      </td>
      <td className="text-right">
        <Decimal amount={reserve} round={6} />
      </td>
      <td className="text-center">
        <div className="flex jc-c portfolio__private-transfer-actions">
          <Button
            onClick={moveToReserve}
            disabled={spendable === 0n}
            className="button--outline button--grey mr-0.5 portfolio__private-transfer-btn"
            data-tooltip-id="global-tip"
            data-tooltip-content="Move from Spending to Private Reserve">
            <Icons.ArrowLeft />
          </Button>
          <Button
            onClick={makeSpendable}
            disabled={reserve === 0n}
            className="button--outline button--grey portfolio__private-transfer-btn"
            data-tooltip-id="global-tip"
            data-tooltip-content="Move from Private Reserve to Spending">
            <Icons.ArrowRight />
          </Button>
        </div>
      </td>
      <td className="text-right">
        <Decimal amount={spendable} round={6} />
      </td>
      <td className="text-right">
        {price?.current ? <>${priceFormatter(price.current)}</> : null}
      </td>
      <td className="text-right">
        <div
          className={clsx({
            "flex ai-c jc-e": true,
            "color-teal": price?.changeDay && price.changeDay > 0,
            "color-red": price?.changeDay && price.changeDay < 0,
          })}>
          {price?.changeDay ? (
            <>
              {price.changeDay.toLocaleDecimal(2)}%
              {price.changeDay > 0 ? (
                <TrendUp className="h-2 w-a ml-0.5" />
              ) : (
                <TrendDown className="h-2 w-a ml-0.5" />
              )}
            </>
          ) : null}
        </div>
      </td>
      <td className="text-right">
        <Fiat
          amount={totalValueUsd}
          decimals={8}
          symbol="$"
          padSymbol={false}
        />
      </td>
      <td className="text-right">
        <Popout buttonClassName="button--outline button--grey mr-0.5">
          <TokenActions balance={subBalance || proxyBalance!} />
        </Popout>
      </td>
    </tr>
  );
};

const KeplrBtcWarning = () => {
  const { t } = useTranslation("portfolio");
  const [infoMsgDismissals, setInfoMsgDismissals] = useLocalStorage<string[]>(
    "keplr-btc-info-dismissals",
    []
  );

  const { accounts, selected } = useWalletAccounts();
  const { balance } = usePreloadedBalance();
  const btcAddress = accounts?.find(
    ({ address, provider }) =>
      provider === "Keplr" && address.networks.includes(BTC)
  )?.address.address;

  if (!!balance?.balance) return null;
  if (selected?.provider !== "Keplr") return null;
  if (infoMsgDismissals?.some((s) => s === selected.address.address))
    return null;

  return (
    <tr>
      <td colSpan={8}>
        <div className="flex jc-c pt-1.5">
          <Warning
            className="warning--sm mb-2 condensed flex jc-c"
            color="orange"
            dismiss={() => {
              if (selected?.address) {
                setInfoMsgDismissals([
                  ...infoMsgDismissals,
                  selected.address.address,
                ]);
              }
            }}>
            <div className="flex ai-c">
              <Animated
                name="exclamation"
                className="color-orange block no-shrink"
                style={{ width: "1.5rem", height: "1.5rem" }}
              />
              <p className="text-left ml-1 lh-19 fs-14">
                {t("keplrBtcWarningIntro")}{" "}
                {btcAddress ? (
                  <>
                    {t("keplrBtcWarningDetected")}{" "}
                    <span className="color-white">{btcAddress}</span>.{" "}
                    {t("keplrBtcWarningEnd")}
                  </>
                ) : (
                  t("keplrBtcWarningNoAddress")
                )}
              </p>
            </div>
          </Warning>
        </div>
      </td>
    </tr>
  );
};

export const LowRuneNotice: FC<{
  network: string;
  symbol: string;
  msg?: string;
}> = ({ network, symbol, msg }) => {
  const { t } = useTranslation("portfolio");
  return (
    <Link to={`../swap/${network}.${symbol}/THOR.RUNE`} className="mb-3 block">
      <Warning
        msg={msg || t("lowRuneWarning")}
        className="fs-12 lh-18 fw-600 color-white warning--pre hover-teal">
        <Icons.NoGas className="color-teal w-1 h-1" />
      </Warning>
    </Link>
  );
};

const Loading: FC = () => {
  return (
    <div className="mt-4 text-center">
      <img src={Anim} alt="" className="block w-12 h-12 mx-a mb-2" />
    </div>
  );
};

const BalanceItem: FC = () => {
  const { t } = useTranslation("portfolio");
  const { balance, asset } = usePreloadedBalance();
  const { accounts } = useWalletAccounts();
  const tooltipId = useId();

  const providerLookup = useMemo(
    () =>
      new Map(
        accounts?.map(({ address, provider }) => [address.address, provider])
      ),
    [accounts]
  );

  const sorted = [...(balance?.accounts || [])].sort((a, b) => {
    if (a.asset.chain === THOR || a.asset.type === "SECURED") return -1;
    if (b.asset.chain === THOR || b.asset.type === "SECURED") return 1;
    return networkLabel(a.asset.chain).localeCompare(
      networkLabel(b.asset.chain)
    );
  });

  const deposit = useDeposit(0n, balance?.asset);

  if (!balance) return null;
  const price = asset.price;

  return (
    <tr>
      <td className="bg-black">
        <IconDenom denom={balance.asset.metadata.symbol} />
      </td>
      <td style={{ paddingLeft: 0 }} className="portfolio__token">
        <div className="flex ai-c no-shrink">
          <h3 className="flex ai-b">
            <AssetLabel
              asset={balance.asset}
              Container={({ children }) => (
                <span className="color-grey fs-14 fs-lg-16">{children}</span>
              )}
            />
          </h3>
          {isMerge(balance.asset.metadata.symbol) && (
            <Link
              to={`../merge/${balance.asset.metadata.symbol}`}
              className="tag tag--md tag--teal mx-1.5">
              {t("mergeTag")}
              <Icons.AngleRight />
            </Link>
          )}
          {isSwitch(balance.asset.metadata.symbol) && (
            <button
              onClick={deposit}
              className="tag tag--md tag--orange mx-1.5">
              {t("migrateTag")}
              <Icons.AngleRight />
            </button>
          )}
        </div>
      </td>
      <td>
        <div className="nowrap flex gap-0.5">
          {sorted.map((x) => {
            const provider = providerLookup.get(x.address);
            const tipId = `${tooltipId}-${x.asset.asset}`;
            return (
              <Fragment key={x.asset.asset}>
                <div data-tooltip-id={tipId}>
                  <NetworkIcon
                    className="w-2.5 h-2.5"
                    network={x.asset.type === "SECURED" ? THOR : x.asset.chain}
                  />
                </div>
                <Tooltip id={tipId} className="tooltip">
                  <div className="flex ai-c gap-1">
                    {provider && (
                      <ProviderIcon
                        provider={provider}
                        selected
                        className="w-3 h-3 block"
                      />
                    )}
                    <div>
                      <div className="condensed fs-16 fw-500">
                        {nFormatter(x.balance, 8, 8)} on {networkLabel(x.asset)}
                      </div>
                    </div>
                  </div>
                </Tooltip>
              </Fragment>
            );
          })}
        </div>
      </td>
      <td className="text-right">
        <Decimal amount={balance.balance} round={6} clip />
      </td>
      <td className="text-right">
        {price?.current ? <>${priceFormatter(price.current)}</> : null}
      </td>
      <td className="text-right">
        <div
          className={clsx({
            "flex ai-c jc-e": true,
            "color-teal": price?.changeDay && price.changeDay > 0,
            "color-red": price?.changeDay && price.changeDay < 0,
          })}>
          {price?.changeDay ? (
            <>
              {price.changeDay.toLocaleDecimal(2)}%
              {price.changeDay > 0 ? (
                <TrendUp className="h-2 w-a ml-0.5" />
              ) : (
                <TrendDown className="h-2 w-a ml-0.5" />
              )}
            </>
          ) : null}
        </div>
      </td>

      <td className="text-right">
        <Fiat
          amount={balance.valueUsd}
          decimals={8}
          symbol="$"
          padSymbol={false}
          clip
        />
      </td>
      <td className="text-right">
        <Popout
          buttonClassName="button--outline button--grey mr-0.5"
          trigger="hover">
          <TokenActions balance={balance} />
        </Popout>
      </td>
    </tr>
  );
};

const TokenActions: FC<{ balance: Balance }> = ({ balance }) => {
  const { t } = useTranslation("portfolio");
  const { isPrivateMode } = useRedacted();
  const v2 = useRedactedV2();
  // Model B: product links as in public Rujira; funding happens on execution.
  const unified = isPrivateMode && v2.modelB.enabled;
  const { subBalance, proxyBalance } = usePreloadedBalance();
  const { showModal, hideModal } = useGlobalModalContext();
  const banxaCapabilities = useBanxaSellCapabilities();
  const banxaSource = getBanxaSellSources([balance], banxaCapabilities)[0];

  const withdraw = () => {
    showModal({
      className: "modal--lg",
      backgroundClose: false,
      children: (
        <TranslationProvider namespace="portfolio">
          <BalanceProvider asset={balance.asset}>
            <TransferSession>
              {(connect) => <Withdraw cancel={hideModal} connect={connect} />}
            </TransferSession>
          </BalanceProvider>
        </TranslationProvider>
      ),
    });
  };

  const deposit = useDeposit(0n, balance.asset);
  const makeSpendable = useDepositToSubWallet(0n, balance.asset);
  const moveToReserve = useWithdrawFromSubWallet(0n, balance.asset);
  const withdrawToPublic = useWithdrawToPublicAccount(0n, balance.asset);

  const send = () => {
    showModal({
      className: "modal--lg",
      backgroundClose: false,
      children: (
        <TranslationProvider namespace="portfolio">
          <BalanceProvider asset={balance.asset}>
            <TransferSession>
              {(connect) => <Send cancel={hideModal} connect={connect} />}
            </TransferSession>
          </BalanceProvider>
        </TranslationProvider>
      ),
    });
  };

  const withdrawWithBanxa = () => {
    if (!banxaCapabilities || !banxaSource) return;
    showModal({
      className: "modal--lg",
      backgroundClose: false,
      children: (
        <BanxaOffRamp
          capabilities={banxaCapabilities}
          initialSourceId={banxaSource.id}
          onBack={hideModal}
        />
      ),
    });
  };

  const { borrowPath, lendPath, stakePath, swapPath, tradePath } =
    useTokenActionRoutes(balance.asset);

  const canWithdraw = !!balance.accounts.find(
    (a) => a.asset.type === "SECURED"
  );
  const canDeposit = !!balance.accounts.find((a) => a.asset.type === "LAYER_1");
  const hasReserveBalance = (proxyBalance?.balance ?? 0n) > 0n;
  const hasSpendingBalance = (subBalance?.balance ?? 0n) > 0n;

  return (
    <nav>
      {!isPrivateMode && canDeposit && (
        <button onClick={deposit}>
          <Icons.Deposit />
          {t("common:deposit")}
        </button>
      )}

      {!isPrivateMode && canWithdraw && (
        <button onClick={withdraw}>
          <Icons.Withdraw />
          {t("common:withdraw")}
        </button>
      )}

      {!isPrivateMode && (
        <button onClick={send}>
          <MoneyTransfer />
          {t("common:send")}
        </button>
      )}
      {(!isPrivateMode || unified) && tradePath && (
        <Link to={tradePath}>
          <Chart />
          {t("trade")}
        </Link>
      )}
      {(!isPrivateMode || unified) && swapPath && (
        <Link to={swapPath}>
          <Swap />
          {t("swap")}
        </Link>
      )}
      {(!isPrivateMode || unified) && stakePath && (
        <Link to={stakePath}>
          <Stake />
          {t("stake")}
        </Link>
      )}
      {(!isPrivateMode || unified) && lendPath && (
        <Link to={lendPath}>
          <Lend />
          {t("lend")}
        </Link>
      )}
      {(!isPrivateMode || unified) && borrowPath && (
        <Link to={borrowPath}>
          <MoneyTransfer />
          {t("borrow")}
        </Link>
      )}
      {!isPrivateMode && isMerge(balance.asset.metadata.symbol) && (
        <Link to={`../merge/${balance.asset.metadata.symbol}`}>
          <Merge />
          {t("merge")}
        </Link>
      )}
      {unified && hasReserveBalance && (
        <button onClick={withdrawToPublic}>
          <Icons.Withdraw />
          {t("common:withdraw")}
        </button>
      )}
      {isPrivateMode && !unified && (
        <>
          {hasReserveBalance && (
            <button onClick={makeSpendable}>
              <Icons.Deposit />
              Make spendable
            </button>
          )}
          {hasSpendingBalance && (
            <button onClick={moveToReserve}>
              <Icons.Withdraw />
              Move to Private Reserve
            </button>
          )}
          {hasReserveBalance && (
            <button onClick={withdrawToPublic}>
              <Icons.Withdraw />
              Withdraw Reserve to Public
            </button>
          )}
          {hasSpendingBalance && tradePath && (
            <Link to={tradePath}>
              <Chart />
              {t("trade")}
            </Link>
          )}
          {hasSpendingBalance && swapPath && (
            <Link to={swapPath}>
              <Swap />
              {t("swap")}
            </Link>
          )}
        </>
      )}

      {/* A fiat off-ramp pays out from a public wallet to a KYC provider. */}
      {!isPrivateMode && banxaSource && (
        <>
          <hr />
          <button onClick={withdrawWithBanxa}>
            <img src={BanxaIcon} alt="Banxa" />
            {t("common:withdrawWithBanxa")}
          </button>
        </>
      )}
    </nav>
  );
};
const isMerge = (symbol: string) => {
  return ["KUJI", "rKUJI", "RKUJI", "FUZN", "WINK", "NSTK", "LVN"].includes(
    symbol
  );
};

const isSwitch = (_symbol: string) => {
  return false;
};

const getStakePath = (symbol: string) => {
  switch (symbol) {
    case "RUJI":
      return stakeRoute("RUJI");
    case "RUNE":
    case "bRUNE":
      return stakeRoute("bRUNE");
    case "TCY":
      return stakeRoute("TCY");
    default:
      return null;
  }
};

const TokenActionRoutesProvider: FC<PropsWithChildren> = ({ children }) => {
  const env = useRelayEnvironment();
  const [data, setData] = useState<TokenActionData>(EMPTY_TOKEN_ACTION_DATA);

  useEffect(() => {
    let cancelled = false;

    fetchQuery<BalancesTokenActionsQuery>(env, tokenActionsQuery, {})
      .toPromise()
      .then((data) => {
        if (!cancelled && data) setData(createTokenActionData(data));
      })
      .catch((error) => {
        console.error("Failed to load portfolio token actions", error);
      });

    return () => {
      cancelled = true;
    };
  }, [env]);

  return (
    <tokenActionDataContext.Provider value={data}>
      {children}
    </tokenActionDataContext.Provider>
  );
};

type TokenActionAsset = {
  readonly chain: string;
  readonly metadata: {
    readonly symbol: string;
  };
};

const getDefaultBorrowDebtSegment = (asset: TokenActionAsset) => {
  const symbol = asset.metadata.symbol.toUpperCase();

  if (symbol === "USDC" || symbol === "USDT") return "BTC";

  return "USDC";
};

const getBorrowPath = (asset: TokenActionAsset) => {
  const collateralSegment = assetToTradeUrlSegment(asset);
  const debtSegment = getDefaultBorrowDebtSegment(asset);

  return `/borrow/${collateralSegment}/${debtSegment}`;
};

const useTokenActionRoutes = (
  asset: TokenActionAsset | null | undefined
): TokenActionRoutes => {
  const { borrowSymbols, lendPaths, swapSymbols, tradePaths } = useContext(
    tokenActionDataContext
  );

  if (!asset) {
    return {
      borrowPath: null,
      lendPath: null,
      stakePath: null,
      swapPath: null,
      tradePath: null,
    };
  }

  const symbol = asset.metadata.symbol;

  return {
    borrowPath: borrowSymbols.has(`${symbol}.${asset.chain}`)
      ? getBorrowPath(asset)
      : null,
    lendPath: lendPaths.get(assetToLendUrlSegment(asset)) ?? null,
    stakePath: getStakePath(symbol),
    swapPath: getSwapPath(symbol, swapSymbols),
    tradePath: tradePaths.get(assetToTradeUrlSegment(asset)) ?? null,
  };
};

const createTokenActionData = (
  data: BalancesTokenActionsQuery["response"]
): TokenActionData => {
  const borrowSymbols = new Set(
    data.ghostCredit?.collaterals.map(
      ({ asset }) => `${asset.metadata.symbol}.${asset.chain}`
    ) || []
  );
  const swapSymbols = getSwapSymbols(data);

  const tradePairs =
    data.finV2.edges?.flatMap((edge) => (edge?.node ? [edge.node] : [])) || [];

  const tradePaths = buildTradePaths(tradePairs);
  const lendPaths = buildLendPaths(data);

  return { borrowSymbols, lendPaths, swapSymbols, tradePaths };
};

const buildLendPaths = (
  data: BalancesTokenActionsQuery["response"]
): Map<string, string> => {
  const map = new Map<string, string>();

  data.strategies.edges?.forEach((edge) => {
    if (edge?.node?.__typename !== "GhostVault") return;
    map.set(assetToLendUrlSegment(edge.node.asset), lendRoute(edge.node.asset));
  });

  return map;
};

const getSwapSymbols = (data: BalancesTokenActionsQuery["response"]) => {
  const symbols = new Set<string>(["RUNE"]);

  data.thorchainV2?.pools.forEach(({ asset, status }) => {
    if (status !== "AVAILABLE") return;
    symbols.add(asset.metadata.symbol);
    if (asset.variants.secured) {
      symbols.add(asset.variants.secured.metadata.symbol);
    }
  });

  return symbols;
};

const getSwapPath = (symbol: string, symbols: Set<string>) => {
  if (!symbols.has(symbol)) return null;

  const target =
    ["BTC", "ETH", "USDC", "RUNE"].find(
      (candidate) => candidate !== symbol && symbols.has(candidate)
    ) || [...symbols].find((candidate) => candidate !== symbol);

  return target ? `/swap/${symbol}/${target}` : null;
};

const buildTradePaths = (pairs: TokenActionPair[]): Map<string, string> => {
  const assetIds = new Set<string>();
  pairs.forEach((pair) => {
    assetIds.add(assetToTradeUrlSegment(pair.assetBase));
    assetIds.add(assetToTradeUrlSegment(pair.assetQuote));
  });

  const map = new Map<string, string>();
  assetIds.forEach((assetId) => {
    const pair = getPreferredTradePair(assetId, pairs);
    if (pair) {
      map.set(
        assetId,
        `/trade/${assetToTradeUrlSegment(pair.assetBase)}/${assetToTradeUrlSegment(pair.assetQuote)}`
      );
    }
  });

  return map;
};

type TokenActionPair = NonNullable<
  NonNullable<
    NonNullable<BalancesTokenActionsQuery["response"]["finV2"]["edges"]>[number]
  >["node"]
>;

const getPreferredTradePair = (assetId: string, pairs: TokenActionPair[]) => {
  const candidates = pairs.filter((pair) => {
    const { base, quote } = getTradePairAssetIds(pair);

    return base === assetId || quote === assetId;
  });

  if (["USDC", "USDT"].includes(assetId.split(".")[0])) {
    return (
      findTradePair(pairs, "BTC", assetId) ||
      candidates.find((pair) => getTradePairAssetIds(pair).quote === assetId) ||
      candidates[0]
    );
  }

  return (
    findTradePair(pairs, assetId, "USDC") ||
    findTradePair(pairs, assetId, "USDT") ||
    candidates[0]
  );
};

const findTradePair = (
  pairs: TokenActionPair[],
  baseId: string,
  quoteId: string
) =>
  pairs.find((pair) => {
    const ids = getTradePairAssetIds(pair);

    return ids.base === baseId && ids.quote === quoteId;
  });

const getTradePairAssetIds = (pair: TokenActionPair) => ({
  base: assetToTradeUrlSegment(pair.assetBase),
  quote: assetToTradeUrlSegment(pair.assetQuote),
});
